import { memo, MouseEvent, ReactNode, useCallback, useEffect, useRef, useState } from "react";

import cls from "./Popup.module.scss";
import { Portal } from "../Portal";
import { useHover } from "shared/lib/hooks/useHover/useHover";
import { classNames } from "shared/lib/classNames/classNames";

interface PopupProps {
    className?: string;
    text?: ReactNode;
    children?: ReactNode;
    width?: string;
    background?: string;
    dangerouslySetInnerHTML?: string;
}

const GAP_PX = 8;

export const Popup = memo((props: PopupProps) => {
    const { className, children, text, width, background = "#fff", dangerouslySetInnerHTML } = props;
    const iconRef = useRef<HTMLDivElement>(null);
    const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
    const latestLeftRef = useRef<number | null>(null);
    const rafRef = useRef<number | null>(null);

    const [isHover, bindHover] = useHover();

    const updateTop = useCallback(() => {
        if (!iconRef.current) return;

        const rect = iconRef.current.getBoundingClientRect();
        setCoords((prev) => {
            const left = latestLeftRef.current ?? prev?.left ?? rect.left + rect.width / 2;
            return { top: rect.top, left };
        });
    }, []);

    useEffect(() => {
        if (!isHover) return;
        updateTop();
    }, [isHover, updateTop]);

    useEffect(() => {
        if (!isHover) return;

        const onScrollOrResize = () => updateTop();
        window.addEventListener("scroll", onScrollOrResize, true);
        window.addEventListener("resize", onScrollOrResize);
        return () => {
            window.removeEventListener("scroll", onScrollOrResize, true);
            window.removeEventListener("resize", onScrollOrResize);
        };
    }, [isHover, updateTop]);

    const onMouseEnter = useCallback(
        (e: MouseEvent<HTMLDivElement>) => {
            bindHover.onMouseEnter();
            latestLeftRef.current = e.clientX;
            updateTop();
        },
        [bindHover, updateTop],
    );

    const onMouseLeave = useCallback(() => {
        bindHover.onMouseLeave();
        latestLeftRef.current = null;
        if (rafRef.current) {
            window.cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        }
    }, [bindHover]);

    const onMouseMove = useCallback((e: MouseEvent<HTMLDivElement>) => {
        latestLeftRef.current = e.clientX;
        if (rafRef.current) return;
        rafRef.current = window.requestAnimationFrame(() => {
            rafRef.current = null;
            setCoords((prev) => (prev ? { ...prev, left: latestLeftRef.current ?? prev.left } : prev));
        });
    }, []);

    return (
        <>
            <div
                style={{ width }}
                ref={iconRef}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                onMouseMove={onMouseMove}
                className={cls.popupWrapper}
            >
                {children}
            </div>

            {isHover && coords && (
                <Portal>
                    <div
                        className={classNames(cls.popup, { [cls.visible]: isHover }, [className])}
                        style={{
                            position: "fixed",
                            top: coords.top - GAP_PX,
                            left: coords.left,
                            transform: "translate(-50%, -100%)",
                            zIndex: 9999,
                            // Use a CSS variable so the arrow (::after) matches the background.
                            ["--popup-bg" as any]: background,
                            boxShadow: background ? "none" : "0 2px 8px rgba(0,0,0,0.15)",
                        }}
                    >
                        {dangerouslySetInnerHTML ? (
                            <div dangerouslySetInnerHTML={{ __html: dangerouslySetInnerHTML }} />
                        ) : (
                            text
                        )}
                    </div>
                </Portal>
            )}
        </>
    );
});

Popup.displayName = "Popup";
